import { CheckIcon, XIcon } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { MONTH_NAMES_SV, resolveTimeText } from '@/utils/resolve-time-text'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'

type PendingEvent = {
  id?: string
  description: string
  timeText: string
  year?: number
  month?: number
}

type ConfirmedDate = {
  year: number
  month?: number
  day?: number
}

type Props = {
  event: PendingEvent
  birthDate?: Date
  /** Locks the confirm/dismiss actions; the event itself stays readable. */
  isDisabled?: boolean
  onConfirm: (eventId: string, date: ConfirmedDate) => void
  onDismiss: (eventId: string) => void
}

const CURRENT_YEAR = new Date().getFullYear()
const YEAR_OPTIONS = Array.from({ length: 120 }, (_, i) => CURRENT_YEAR - i)

export function PendingEventCard({
  event,
  birthDate,
  isDisabled = false,
  onConfirm,
  onDismiss,
}: Props) {
  const { t } = useTranslation()

  const resolved = resolveTimeText(event.timeText, new Date(), birthDate)
  const initialYear = event.year ?? resolved?.year
  const initialMonth = event.month ?? resolved?.month

  const [year, setYear] = useState<number | undefined>(initialYear)
  const [month, setMonth] = useState<number | undefined>(initialMonth)
  const [day, setDay] = useState<number | undefined>(undefined)

  const eventId = event.id ?? event.description

  const handleConfirm = () => {
    if (!year) return
    onConfirm(eventId, { year, month, day: day || undefined })
  }

  return (
    <div className="rounded-lg border bg-layer-1 p-4 flex flex-col sm:flex-row sm:items-center gap-3">
      {/* Left: description + time text */}
      <div className="flex-1 min-w-0">
        <p className="font-medium text-sm leading-snug">{event.description}</p>
        <p className="text-xs text-text-secondary mt-0.5">{event.timeText}</p>
      </div>

      {/* Right: date controls + actions */}
      <div className="flex flex-wrap items-end gap-2 shrink-0">
        {/* Year */}
        <div className="space-y-1 min-w-[90px]">
          <p className="text-xs text-text-secondary">{t('notesPage.detail.pendingEvents.year')}</p>
          <Select onValueChange={v => setYear(Number(v))} value={year ? String(year) : 'none'}>
            <SelectTrigger className="h-9 text-sm">
              <SelectValue placeholder="—" />
            </SelectTrigger>
            <SelectContent>
              {YEAR_OPTIONS.map(y => (
                <SelectItem key={y} value={String(y)}>
                  {y}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Month */}
        <div className="space-y-1 min-w-[130px]">
          <p className="text-xs text-text-secondary">{t('notesPage.detail.pendingEvents.month')}</p>
          <Select
            onValueChange={v => setMonth(v === 'none' ? undefined : Number(v))}
            value={month ? String(month) : 'none'}
          >
            <SelectTrigger className="h-9 text-sm">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none">—</SelectItem>
              {Object.entries(MONTH_NAMES_SV).map(([num, name]) => (
                <SelectItem key={num} value={num}>
                  {name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Day (optional) */}
        <div className="space-y-1 w-[80px]">
          <p className={cn('text-xs', month ? 'text-text-secondary' : 'text-text-tertiary')}>
            {t('notesPage.detail.pendingEvents.day')}
          </p>
          <Input
            className="h-9 text-sm [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
            disabled={isDisabled || !month}
            max={31}
            min={1}
            onChange={e => {
              const val = parseInt(e.target.value, 10)
              setDay(isNaN(val) ? undefined : val)
            }}
            placeholder="—"
            type="number"
            value={day ?? ''}
          />
        </div>

        <Button
          className="h-9 shrink-0"
          disabled={isDisabled || !year}
          onClick={handleConfirm}
          size="sm"
        >
          <CheckIcon className="size-4" />
          {t('notesPage.detail.pendingEvents.confirm')}
        </Button>

        <Button
          className="shrink-0"
          onClick={() => onDismiss(eventId)}
          size="icon"
          variant="ghost"
        >
          <XIcon className="size-4 text-text-secondary" />
        </Button>
      </div>
    </div>
  )
}
